'use client';

import * as React from 'react';
import { cn } from '../../lib/cn';
import { type LinkLike, NativeLink } from '../../lib/link';
import { Text } from '../text/text';

/**
 * Atomi condivisi per le sidebar dell'app (classica e PRO). Geometria e spaziature
 * vengono dal mock `SLIMS-CER_v11.html` (`.sidebar-label`, `.nav-item`, `.kpi-card`):
 * voci 11px/13px con raggio 9px e bordo sinistro sull'attivo, etichette di gruppo a
 * 11px con `letter-spacing: 2px`. L'accento è parametrico: `accent` (ciano,
 * interfaccia classica) o `brand` (la tinta del chrome, ambra di default).
 *
 * Le altezze del mock nascono dal suo `line-height: 1.6` di body, che qui non c'è
 * (la base Tailwind è 1.5 e le varianti di `Text` hanno leading propri): dove la
 * misura conta — etichetta di gruppo e voce — l'interlinea è riportata a 1.6, così
 * la voce torna alta 44px e il passo fra due voci 48px come nel mock.
 */

export type SidebarAccent = 'accent' | 'brand';

export type SidebarBadgeTone = 'green' | 'blue' | 'amber' | 'red' | 'purple' | 'muted';

const BADGE_TONE: Record<SidebarBadgeTone, string> = {
	green: 'bg-accent-green/20 text-accent-green',
	blue: 'bg-accent/10 text-accent',
	amber: 'bg-warn/20 text-warn',
	red: 'bg-danger/20 text-danger',
	purple: 'bg-accent-purple/20 text-accent-purple',
	muted: 'bg-border/40 text-text-muted',
};

export interface SidebarBadgeProps {
	tone?: SidebarBadgeTone;
	children: React.ReactNode;
}

/** Contatore/etichetta compatta a fianco di una voce di navigazione. */
export function SidebarBadge({ tone = 'muted', children }: SidebarBadgeProps) {
	return (
		<Text
			as="span"
			variant="caption"
			tone="inherit"
			className={cn(
				'flex-none whitespace-nowrap rounded-md px-1.5 py-px font-mono',
				BADGE_TONE[tone],
			)}
		>
			{children}
		</Text>
	);
}

export interface SidebarGroupLabelProps {
	children: React.ReactNode;
}

/** Etichetta maiuscola di un gruppo di voci. */
export function SidebarGroupLabel({ children }: SidebarGroupLabelProps) {
	return (
		<Text
			as="div"
			variant="label"
			tone="muted"
			weight="normal"
			className="mb-[5px] px-1.5 text-[11px] leading-[1.6] tracking-[2px]"
		>
			{children}
		</Text>
	);
}

export interface SidebarGroupProps {
	label?: string;
	children: React.ReactNode;
	/** Sidebar compressa: il gruppo stringe i fianchi per lasciare spazio alle sole icone. */
	collapsed?: boolean;
}

/** Gruppo di voci con etichetta opzionale. */
export function SidebarGroup({ label, children, collapsed = false }: SidebarGroupProps) {
	return (
		<div className={collapsed ? 'px-1 pb-2' : 'px-3.5 pb-3.5'}>
			{label ? <SidebarGroupLabel>{label}</SidebarGroupLabel> : null}
			{children}
		</div>
	);
}

/**
 * Voce attiva: il bordo sinistro di 2px del mock mangia il box (`box-sizing: border-box`),
 * quindi il padding sinistro scende da 13px a 11px e l'etichetta resta incolonnata con
 * quelle delle voci inattive.
 *
 * Il fondo tenue usa `accent-tint` e non `accent`: il mock tiene i due separati
 * (`rgba(0,174,239,.1)` per il fondo, `var(--accent)` per testo e bordo), così in
 * tema chiaro il testo si scurisce ma la campitura resta il blu di marca.
 */
const ITEM_ACCENT: Record<SidebarAccent, { active: string; idle: string }> = {
	brand: {
		active: 'border-l-2 border-brand bg-brand/10 pl-[11px] pr-[13px] text-brand',
		idle: 'px-[13px] text-text-muted hover:bg-brand/[.06] hover:text-brand',
	},
	accent: {
		active: 'border-l-2 border-accent bg-accent-tint/10 pl-[11px] pr-[13px] text-accent',
		idle: 'px-[13px] text-text-muted hover:bg-accent-tint/[.06] hover:text-accent',
	},
};

export interface SidebarItemProps {
	label: string;
	/** Slot icona (lucide, emoji, …); dimensionato dal chiamante. */
	icon?: React.ReactNode;
	active?: boolean;
	accent?: SidebarAccent;
	/** Badge a destra dell'etichetta (tipicamente `SidebarBadge`). */
	badge?: React.ReactNode;
	/** Elemento finale (es. chevron di espansione). */
	trailing?: React.ReactNode;
	/** Modalità compressa: solo icona centrata. */
	collapsed?: boolean;
	/** Se presente rende un link (via `linkComponent`), altrimenti un button. */
	href?: string;
	linkComponent?: LinkLike;
	onClick?: () => void;
	title?: string;
}

/** Voce di navigazione della sidebar (stile PRO: attivo con bordo sinistro). */
export function SidebarItem({
	label,
	icon,
	active = false,
	accent = 'accent',
	badge,
	trailing,
	collapsed = false,
	href,
	linkComponent: Link = NativeLink,
	onClick,
	title,
}: SidebarItemProps) {
	const className = cn(
		'mb-1 flex w-full items-center gap-[11px] rounded-nav py-[11px] text-left transition-colors',
		active ? ITEM_ACCENT[accent].active : ITEM_ACCENT[accent].idle,
		collapsed && 'justify-center rounded-none px-0',
	);
	const content = (
		<>
			{icon ? <span className="flex-none">{icon}</span> : null}
			{!collapsed && (
				<>
					<Text
						as="span"
						variant="subtitle"
						weight="normal"
						tone="inherit"
						truncate
						className="min-w-0 flex-1 leading-[1.6]"
					>
						{label}
					</Text>
					{badge}
					{trailing}
				</>
			)}
		</>
	);
	if (href) {
		return (
			<Link
				href={href}
				className={className}
				title={title}
				onClick={onClick}
				aria-current={active ? 'page' : undefined}
			>
				{content}
			</Link>
		);
	}
	return (
		<button type="button" className={className} title={title} onClick={onClick}>
			{content}
		</button>
	);
}

export interface SidebarKpiProps {
	label: string;
	/** Valore già formattato; vuoto → em dash. */
	value?: string;
	detail?: React.ReactNode;
}

/** Mini card KPI ancorata in fondo alla sidebar. */
export function SidebarKpi({ label, value, detail }: SidebarKpiProps) {
	return (
		<div className="mb-[7px] rounded-nav border border-border bg-card p-2.5">
			<Text
				as="div"
				variant="label"
				tone="muted"
				weight="normal"
				className="mb-[3px] text-[11px] tracking-[1px]"
			>
				{label}
			</Text>
			<Text as="div" variant="dataMd" tone="accent" className="text-[20px]">
				{value || '-'}
			</Text>
			{detail ? (
				<Text as="div" variant="caption" tone="muted" className="mt-0.5">
					{detail}
				</Text>
			) : null}
		</div>
	);
}
